import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, apiEndpoints } from '../../utils/apiService';
import {
  parseOrderingErrorBody,
  parseOrderingParam,
  type TableSortState,
  toggleSortState,
  toOrderingParam,
} from '../../utils/listOrdering';
import {
  SENT_NOTIFICATIONS_PAGE_SIZE,
  readSentNotificationsPage,
  type SentNotification,
} from './adminSentNotificationsModel';
import {
  PA_URL_SENT_NOTIFICATIONS,
  SENT_NOTIFICATION_FIELD_TO_SORT_KEY,
  SENT_NOTIFICATION_SORT_KEY_TO_FIELD,
  type SentNotificationSortKey,
} from './platformAdminOrdering';

const NO_SORT: TableSortState<SentNotificationSortKey> = {
  key: null,
  direction: 'desc',
};

/**
 * Sent-notification log. Kept beside the tab so the page, search and sort
 * survive switching away and back inside platform admin.
 */
export function useAdminSentNotifications(active: boolean) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<SentNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [settled, setSettled] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [listError, setListError] = useState<string | null>(null);

  const sort: TableSortState<SentNotificationSortKey> = useMemo(
    () =>
      parseOrderingParam(
        searchParams.get(PA_URL_SENT_NOTIFICATIONS),
        SENT_NOTIFICATION_FIELD_TO_SORT_KEY,
      ) ?? NO_SORT,
    [searchParams],
  );

  const orderingApi = useMemo(
    () => toOrderingParam(sort, SENT_NOTIFICATION_SORT_KEY_TO_FIELD),
    [sort],
  );

  const toggleSort = useCallback(
    (key: SentNotificationSortKey) => {
      setPage(1);
      setListError(null);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const current =
          parseOrderingParam(
            prev.get(PA_URL_SENT_NOTIFICATIONS),
            SENT_NOTIFICATION_FIELD_TO_SORT_KEY,
          ) ?? NO_SORT;
        const toggled = toggleSortState(current, key);
        const param = toOrderingParam(
          toggled,
          SENT_NOTIFICATION_SORT_KEY_TO_FIELD,
        );
        if (param) next.set(PA_URL_SENT_NOTIFICATIONS, param);
        else next.delete(PA_URL_SENT_NOTIFICATIONS);
        return next;
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(1);
    setListError(null);
  };

  const fetchRows = useCallback(
    async (pageToLoad = 1) => {
      setLoading(true);
      setListError(null);
      try {
        const response = await api.get(
          apiEndpoints.getAdminSentNotifications(
            pageToLoad,
            SENT_NOTIFICATIONS_PAGE_SIZE,
            orderingApi ?? undefined,
            debouncedSearch ? { search: debouncedSearch } : undefined,
          ),
        );
        if (response && response.ok) {
          const result = readSentNotificationsPage(await response.json());
          setRows(result.rows);
          if (result.count !== undefined) setCount(result.count);
          if (result.totalPages !== undefined) {
            setTotalPages(result.totalPages);
          }
        } else if (response?.status === 400) {
          const body = await response.json().catch(() => ({}));
          setRows([]);
          setCount(0);
          setTotalPages(1);
          setListError(
            parseOrderingErrorBody(body) || 'Could not apply that sort.',
          );
        } else {
          setRows([]);
          setListError('Could not load sent notifications.');
        }
      } catch {
        setRows([]);
        setListError('Could not load sent notifications.');
      } finally {
        setLoading(false);
        setSettled(true);
      }
    },
    [orderingApi, debouncedSearch],
  );

  useEffect(() => {
    if (active) fetchRows(page);
  }, [active, page, fetchRows]);

  return {
    rows,
    // Until the first fetch settles, empty rows mean "not loaded yet".
    loading: loading || !settled,
    page,
    totalPages,
    count,
    search,
    appliedSearch: debouncedSearch,
    listError,
    sort,
    setPage,
    toggleSort,
    onSearchChange,
  };
}

export type AdminSentNotificationsState = ReturnType<
  typeof useAdminSentNotifications
>;
